import { Col, Row, Select, Button, Breadcrumb, Space, Spin, notification } from "antd";
import { useContext, useEffect, useState, useCallback } from "react";
import { ArrowLeftOutlined, ClearOutlined } from '@ant-design/icons';
import GlobalContex from "../../../Store/GlobalContext";
import useFetch from "../../../hooks/useFetch";
import { FetchApiInterface } from "../../../interfaces/FetchApi.interface";
/*
// Especificaciones para el componente
1 Variable de entrada para definir el nivel que debe llegar accessLevel:
 "Region", "Country", "Pu", "Level 4", "Level 5", "Level 6" ..... "Level 8" y adicional si esta variable es null o vacia se entiende que debe llegar al ultimo nivel disponible
2 variable de entrada isRequired para definir si es obligatorio o no por defecto es false
3 variable multiple para definir si se puede seleccionar mas de un valor o no por defecto es false
4 variable name para definir el nombre del campo por defecto es 'dpa'
5 Se debe buscar la manera de regresar al nivel anterior de donde se partio
6 Se debe buscar la manera de limpiar el campo y regresar al inicio permitido
7 como llamada inicial se sebe obtener la sesion del usuario y en base al rol debe hacer la llamada inicial
// ROLES
 estos roles consumen el endpoint /catalogs/dpa/get_dpa
ROLE_GLOBAL_ADMIN, ROLE_SYSADMIN, ROLE_GLOBAL_ADMIN -> estos roles son de nivel superior y consumen todos los niveles pasando dpaType: "Region"
ROLE_REGION_ADMIN -> este rol es de nivel medio y consume todos los niveles pasando dpaFatherId: dpaIdRegion -> el dpaIdRegion se lo obtiene de la sesion del usuario
ROLE_COUNTRY_ADMIN -> este rol es de nivel inferior y consume todos los niveles pasando dpaFatherId: dpaIdCountry -> el dpaIdCountry se lo obtiene de la sesion del usuario
-------------------------------------------
ROLE_USER, ROLE_GUEST, ROLE_COUNTRY_PROCESS -> estos roles son de nivel inferior y deben consumir e endpoint /users/get_communities sin pasar ningun argumento..
 
8 Si se pasa un dpaFatherId se debe buscar en base a el y no lanzar la llamada inicial automaticamente en base a los roles
9 Es importante que en cada seleccion que se haga debe retonar el dpaId y el dpaName al padre para que pueda mostrarlos en la UI esto siempre y cuando no se haya definido el accessLevel
10 Se puede definir un maxLevel para definir el nivel maximo que debe llegar pero en este caso puede o no llegar a ese nivel y en dado caso aqui si puede ir retornando el dpaId y el dpaName al padre para que pueda mostrarlos en la UI

*/

// Interfaces para las respuestas de la API
interface DpaType {
  dtyId: number;
  dtyName: string;
  dtyOrder: number;
}

interface DpaItem {
  statusRegister: boolean;
  createdAt: string;
  updatedAt: string | null;
  dpaId: number;
  dpaName: string;
  dpaCode: string;
  dpaLatitude: number | null;
  dpaLongitude: number | null;
  dpaIsNull: boolean;
  dpaType: DpaType;
  dpaFather: DpaItem | null;
  count: number | null;
}

interface ApiResponse {
  status: string;
  code: number;
  data: DpaItem[];
  message: string | null;
}

interface RegionDpaIdsProps {
  accessLevel?: string; // "Region", "Country", "Pu", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8"
  isRequired?: boolean;
  multiple?: boolean;
  name?: string;
  dpaFatherId?: number; // Si se pasa, no usar lógica de roles
  maxLevel?: string;
  onChange?: (dpaId: number | number[], dpaName: string | string[]) => void;
  value?: number | number[];
  getOnChangeValue?: boolean; // Si es true, siempre retorna el valor seleccionado sin importar el nivel
}

const RegionDpaIds: React.FC<RegionDpaIdsProps> = ({
  accessLevel,
  isRequired = false,
  multiple = false,
  name = 'dpa',
  dpaFatherId,
  maxLevel,
  onChange,
  value,
  getOnChangeValue = false
}) => {
  const { user: { auth } } = useContext(GlobalContex);
  const { fetchApi, loading } = useFetch();
  
  const [dpaOptions, setDpaOptions] = useState<DpaItem[]>([]);
  const [selectedPath, setSelectedPath] = useState<DpaItem[]>([]);
  const [currentLevel, setCurrentLevel] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<number | number[] | undefined>(value);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isAtFinalLevelState, setIsAtFinalLevelState] = useState(false);

  // Mapeo de niveles de acceso
  const levelOrder = {
    'Region': 1,
    'Country': 2,
    'Pu': 3,
    'Level 4': 4,
    'Level 5': 5,
    'Level 6': 6,
    'Level 7': 7,
    'Level 8': 8
  };

  // Determinar si el usuario puede acceder a cierto nivel
  const canAccessLevel = useCallback((level: string): boolean => {
    if (!accessLevel) return true;
    const targetOrder = levelOrder[accessLevel as keyof typeof levelOrder];
    const currentOrder = levelOrder[level as keyof typeof levelOrder];
    return currentOrder <= targetOrder;
  }, [accessLevel]);

  // Determinar si hemos llegado al nivel máximo permitido
  const hasReachedMaxLevel = useCallback((level: string): boolean => {
    if (maxLevel) {
      const maxOrder = levelOrder[maxLevel as keyof typeof levelOrder];
      const currentOrder = levelOrder[level as keyof typeof levelOrder];
      return currentOrder >= maxOrder;
    }
    if (accessLevel) {
      const accessOrder = levelOrder[accessLevel as keyof typeof levelOrder];
      const currentOrder = levelOrder[level as keyof typeof levelOrder];
      return currentOrder >= accessOrder;
    }
    return false;
  }, [maxLevel, accessLevel]);

  // Obtener datos DPA basado en roles
  const fetchDpaData = useCallback(async (params: any = {}) => {
    setIsLoadingData(true);
    try {
      let endpoint = '/catalogs/dpa/get_dpa';
      let requestParams: any = { statusRegister: true };

      // Si se pasa dpaFatherId en params, usarlo directamente (navegación)
      if (params.dpaFatherId) {
        requestParams.dpaFatherId = params.dpaFatherId;
      } 
      // Si dpaFatherId está definido como prop, usarlo directamente
      else if (dpaFatherId) {
        requestParams.dpaFatherId = dpaFatherId;
      } 
      // Lógica basada en roles (solo para carga inicial)
      else {
        const userRoles = auth?.roles || [];
        
        if (userRoles.includes('ROLE_GLOBAL_ADMIN') || 
            userRoles.includes('ROLE_SYSADMIN')) {
          // Roles de nivel superior - obtener regiones
          requestParams.dpaType = 'Region';
        } else if (userRoles.includes('ROLE_REGION_ADMIN')) {
          // Rol de región - usar dpaIdRegion del usuario
          if (auth?.user?.dpa?.dpaIdRegion) {
            requestParams.dpaFatherId = auth.user.dpa.dpaIdRegion;
          }
        } else if (userRoles.includes('ROLE_COUNTRY_ADMIN') || userRoles.includes('ROLE_COUNTRY_PROCESS')) {
          // Rol de país - usar dpaIdCountry del usuario
          if (auth?.user?.dpa?.dpaIdCountry) {
            requestParams.dpaFatherId = auth.user.dpa.dpaIdCountry;
          }
        } else if (userRoles.includes('ROLE_USER') || 
                   userRoles.includes('ROLE_GUEST')) {
          // Roles de nivel inferior - usar endpoint de comunidades
          endpoint = '/users/get_communities';
          requestParams = {}; // Sin parámetros para este endpoint
        }
      }

      const dataSend: FetchApiInterface = {
        method: 'POST',
        url: endpoint,
        data: requestParams
      };

      const response = await fetchApi(dataSend);
      
      if (response?.data?.status === 'success') {
        const dpaData = response.data.data || [];
        setDpaOptions(dpaData);
        
        // Establecer el nivel actual basado en el primer elemento
        if (dpaData.length > 0) {
          const levelName = dpaData[0].dpaType.dtyName;
          setCurrentLevel(levelName);
          
          // Verificar si hemos llegado al accessLevel definido
          if (accessLevel && levelName === accessLevel) {
            setIsAtFinalLevelState(true);
            setSelectedValue(multiple ? [] : undefined); // Limpiar selección previa
          } else {
            setIsAtFinalLevelState(false);
          }
        }
      } else {
        notification.error({
          message: 'Error',
          description: 'Error al cargar los datos DPA'
        });
      }
    } catch (error) {
      console.error('Error fetching DPA data:', error);
      notification.error({
        message: 'Error',
        description: 'Error al procesar la solicitud'
      });
    } finally {
      setIsLoadingData(false);
    }
  }, [auth, dpaFatherId, fetchApi]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchDpaData();
  }, []);

  // Determinar si estamos en el nivel final donde se permite selección múltiple
  const isAtFinalLevel = useCallback((levelName: string): boolean => {
    if (accessLevel) {
      return levelName === accessLevel;
    }
    if (maxLevel) {
      return levelName === maxLevel;
    }
    // Si no hay accessLevel ni maxLevel definidos, verificar si no hay más niveles disponibles
    return false;
  }, [accessLevel, maxLevel]);

  // Verificar si el siguiente nivel tiene datos (llamada de prueba)
  const checkNextLevelHasData = async (dpaId: number): Promise<boolean> => {
    try {
      const dataSend: FetchApiInterface = {
        method: 'POST',
        url: '/catalogs/dpa/get_dpa',
        data: { statusRegister: true, dpaFatherId: dpaId }
      };

      const response = await fetchApi(dataSend);
      
      if (response?.data?.status === 'success') {
        const dpaData = response.data.data || [];
        return dpaData.length > 0;
      }
      return false;
    } catch (error) {
      console.error('Error checking next level:', error);
      return false;
    }
  };

  // Manejar selección de DPA
  const handleDpaSelect = async (dpaId: number | number[]) => {
    // Si getOnChangeValue está en true, siempre retornar el valor seleccionado
    if (getOnChangeValue && onChange) {
      const selectedItems = dpaOptions.filter(item => 
        Array.isArray(dpaId) ? dpaId.includes(item.dpaId) : item.dpaId === dpaId
      );
      const names = selectedItems.map(item => item.dpaName);
      onChange(dpaId, names);
    }

    // Verificar si estamos en el nivel final donde se permite selección múltiple
    const currentLevelIsFinal = isAtFinalLevel(currentLevel) || 
                               hasReachedMaxLevel(currentLevel);

    // Si estamos en el nivel final definido por accessLevel y multiple está habilitado
    if (multiple && currentLevelIsFinal) {
      setIsAtFinalLevelState(true);
      setSelectedValue(dpaId);
      if (onChange && !getOnChangeValue) {
        const selectedItems = dpaOptions.filter(item => 
          Array.isArray(dpaId) ? dpaId.includes(item.dpaId) : item.dpaId === dpaId
        );
        const names = selectedItems.map(item => item.dpaName);
        onChange(dpaId, names);
      }
      return;
    }

    // Navegación normal (selección única)
    const selectedDpa = dpaOptions.find(item => 
      Array.isArray(dpaId) ? item.dpaId === dpaId[0] : item.dpaId === dpaId
    );
    if (!selectedDpa) return;

    // Verificar si podemos continuar basado en accessLevel/maxLevel
    const levelBasedCanContinue = canAccessLevel(getNextLevel(selectedDpa.dpaType.dtyName)) &&
                                 !hasReachedMaxLevel(selectedDpa.dpaType.dtyName) &&
                                 !isAtFinalLevel(selectedDpa.dpaType.dtyName);

    if (!levelBasedCanContinue) {
      // No podemos continuar por restricciones de nivel - este es el nivel final
      setIsAtFinalLevelState(true);
      
      if (multiple) {
        // Si multiple está habilitado, permitir selección múltiple en este nivel
        setSelectedValue(dpaId);
        if (onChange && !getOnChangeValue) {
          const selectedItems = dpaOptions.filter(item => 
            Array.isArray(dpaId) ? dpaId.includes(item.dpaId) : item.dpaId === dpaId
          );
          const names = selectedItems.map(item => item.dpaName);
          onChange(dpaId, names);
        }
      } else {
        // Selección única
        setSelectedValue(selectedDpa.dpaId);
        if (onChange && !getOnChangeValue) {
          onChange(selectedDpa.dpaId, selectedDpa.dpaName);
        }
      }
      return;
    }

    // Solo hacer llamada de prueba si no hemos llegado al accessLevel definido
    setIsLoadingData(true);
    const hasNextLevelData = await checkNextLevelHasData(selectedDpa.dpaId);
    
    if (hasNextLevelData) {
      // Hay datos en el siguiente nivel, navegar normalmente
      const newPath = [...selectedPath, selectedDpa];
      setSelectedPath(newPath);
      setSelectedValue(undefined); // Limpiar selección para el siguiente nivel
      setIsAtFinalLevelState(false); // Reset final level state
      await fetchDpaData({ dpaFatherId: selectedDpa.dpaId });
    } else {
      // No hay datos en el siguiente nivel, este es el nivel final
      // Marcar que estamos en el nivel final y habilitar selección múltiple
      setIsAtFinalLevelState(true);
      
      if (multiple) {
        // Si multiple está habilitado, permitir selección múltiple en este nivel
        setSelectedValue(dpaId);
        if (onChange && !getOnChangeValue) {
          const selectedItems = dpaOptions.filter(item => 
            Array.isArray(dpaId) ? dpaId.includes(item.dpaId) : item.dpaId === dpaId
          );
          const names = selectedItems.map(item => item.dpaName);
          onChange(dpaId, names);
        }
      } else {
        // Selección única
        setSelectedValue(selectedDpa.dpaId);
        if (onChange && !getOnChangeValue) {
          onChange(selectedDpa.dpaId, selectedDpa.dpaName);
        }
      }
    }
    
    setIsLoadingData(false);
  };

  // Obtener el siguiente nivel en la jerarquía
  const getNextLevel = (currentLevel: string): string => {
    const currentOrder = levelOrder[currentLevel as keyof typeof levelOrder];
    const nextOrder = currentOrder + 1;
    const nextLevel = Object.keys(levelOrder).find(
      key => levelOrder[key as keyof typeof levelOrder] === nextOrder
    );
    return nextLevel || '';
  };

  // Regresar al nivel anterior
  const goBack = async () => {
    if (selectedPath.length === 0) return;

    const newPath = selectedPath.slice(0, -1);
    setSelectedPath(newPath);
    setSelectedValue(undefined);
    setIsAtFinalLevelState(false); // Reset final level state

    if (newPath.length === 0) {
      // Volver al inicio
      await fetchDpaData();
    } else {
      // Cargar el nivel del padre anterior
      const parentDpa = newPath[newPath.length - 1];
      await fetchDpaData({ dpaFatherId: parentDpa.dpaId });
    }
  };

  // Limpiar selección y volver al inicio
  const clearSelection = async () => {
    setSelectedPath([]);
    setSelectedValue(undefined);
    setIsAtFinalLevelState(false); // Reset final level state
    if (onChange) {
      onChange(multiple ? [] : 0, multiple ? [] : '');
    }
    await fetchDpaData();
  };

  // Generar breadcrumbs
  const breadcrumbItems = selectedPath.map((item, index) => ({
    title: item.dpaName,
    key: item.dpaId.toString()
  }));

  if (currentLevel) {
    breadcrumbItems.push({
      title: currentLevel,
      key: 'current'
    });
  }

  return (
    <Row gutter={[0, 16]}>
      {/* Breadcrumbs y controles */}
      {selectedPath.length > 0 && (
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <Breadcrumb>
                {breadcrumbItems.map(item => (
                  <Breadcrumb.Item key={item.key}>{item.title}</Breadcrumb.Item>
                ))}
              </Breadcrumb>
            </Col>
            <Col>
              <Space>
                <Button 
                  icon={<ArrowLeftOutlined />} 
                  onClick={goBack}
                  disabled={selectedPath.length === 0 || isLoadingData}
                  size="small"
                >
                  Atrás
                </Button>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={clearSelection}
                  disabled={selectedPath.length === 0 || isLoadingData}
                  size="small"
                >
                  Limpiar
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      )}

      {/* Selector DPA */}
      <Col span={24}>
        <Spin spinning={isLoadingData || loading}>
          <Select
            style={{ width: '100%' }}
            placeholder={`Seleccionar ${currentLevel || 'DPA'}`}
            value={selectedValue}
            onChange={handleDpaSelect}
            mode={multiple && isAtFinalLevelState ? 'multiple' : undefined}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={dpaOptions.map(item => ({
              value: item.dpaId,
              label: `${item.dpaName} (${item.dpaCode})`,
              key: item.dpaId
            }))}
            disabled={isLoadingData || loading}
          />
        </Spin>
      </Col>

      {/* Información del nivel actual */}
      {currentLevel && (
        <Col span={24}>
          <small style={{ color: '#666' }}>
            Nivel actual: {currentLevel}
            {accessLevel && ` | Nivel objetivo: ${accessLevel}`}
            {maxLevel && ` | Nivel máximo: ${maxLevel}`}
          </small>
        </Col>
      )}
    </Row>
  );
};

export default RegionDpaIds;
